import { computed, action } from "mobx";
import BaseStore from "./base";
import http from "@app/lib/http";
import notify from "@app/components/notify";
import events from "@app/lib/store/events";
import { CerGenerationStatus, ProjectStatus, aiTemplateShortName } from "@app/constants";

const POLL_INTERVAL_MS = 5000;

// POST /runs answers with the run flat under `data`, GET /cer/runs/:id nests it
// under `data.run`. Read both here so no caller has to know which.
const unwrapRun = (response) => {
    const run = response?.data?.run || response?.data || {};
    return { id: run.id, status: run.status, errorMessage: run.errorMessage };
};

class CerConfigStore extends BaseStore {
    observable() {
        return {
            visible: false,
            projectId: null,
            // The AI report document being configured. Every request the wizard
            // makes is keyed by it: a project can hold several AI documents and
            // each owns its own config, so this is not optional.
            reportDocumentId: null,
            // Which AI template the document was created from (a registry id —
            // see AI_TEMPLATES in constants.js).
            template: null,
            // Status of the owning project, forwarded by whoever opened the
            // wizard. A draft project has no source documents to generate from.
            projectStatus: null,
            hasPreviousRun: false,
            sources: [],
            loadingSources: false,
            loading: false,
            saving: false,
            running: false,
            config: null,
            validation: null,
            currentStepIndex: 0,
            currentPageIndex: 0,
            polling: false,
            // Config warnings the CER service reported when it accepted the last
            // run ([{ path, message }]). Also persisted on the report document,
            // which is where they stay visible after the wizard closes.
            runWarnings: [],
        };
    }

    // Not observable — cleared by stopPolling(), which reset() cannot reach.
    _pollTimer = null;

    @computed get busy() {
        return this.loading || this.saving || this.running;
    }

    @computed get draft() {
        return this.projectStatus === ProjectStatus.DRAFT;
    }

    // What to call the document being configured, mid-sentence. Every user-facing
    // string in this store reads through it, so none of them says "CER" for a
    // stability study.
    @computed get shortName() {
        return aiTemplateShortName(this.template);
    }

    @computed get canStartRun() {
        return !this.draft && this.validation?.valid === true;
    }

    /**
     * Why the run can't be started yet, or null when it can. Source documents
     * are only reachable once a project is published, so a draft project can
     * never satisfy the config validation — say so instead of just listing
     * "Source Documents" as a missing field.
     */
    @computed get startRunBlockedReason() {
        if (this.draft) {
            return "Publish the project and upload source documents to start generation.";
        }
        if (this.validation?.valid !== true) {
            return "Fill in all the required fields to start a run.";
        }
        return null;
    }

    @action
    async open({
        projectId,
        reportDocumentId = null,
        template = null,
        projectStatus = null,
        hasPreviousRun = false,
    }) {
        if (!projectId || !reportDocumentId) {
            // Without a document there is nothing to address — every config
            // request is scoped to one.
            return;
        }

        this.projectId = projectId;
        this.reportDocumentId = reportDocumentId;
        this.template = template;
        this.projectStatus = projectStatus;
        this.hasPreviousRun = hasPreviousRun;
        this.visible = true;
        this.currentStepIndex = 0;
        this.currentPageIndex = 0;
        // Start from nothing rather than the previously opened document's state:
        // `load()` leaves `config` untouched on a 404, which is exactly what a
        // freshly created document returns.
        this.config = null;
        this.validation = null;
        this.runWarnings = [];
        this.sources = [];
        await this.load();
        await this.refreshValidation();
        await this.loadSources();
    }

    @action
    close() {
        // Deliberately does not stop the run poll: the wizard is modal, and its
        // completion toast is the only signal the user gets from this flow.
        this.visible = false;
        this.currentStepIndex = 0;
        this.currentPageIndex = 0;
        this.reportDocumentId = null;
        this.template = null;
        this.projectStatus = null;
        this.hasPreviousRun = false;
        this.config = null;
        this.validation = null;
        this.runWarnings = [];
        this.sources = [];
    }

    @action
    setStep(index, pageIndex = 0) {
        this.currentStepIndex = index;
        this.currentPageIndex = pageIndex;
    }

    @action
    setPage(pageIndex) {
        this.currentPageIndex = pageIndex;
    }

    /**
     * The config is a sub-resource of the AI report document that owns it — the
     * project alone does not identify one.
     */
    get baseUrl() {
        return `/project/${this.projectId}/report-documents/${this.reportDocumentId}/cer`;
    }

    @action
    async load() {
        if (!this.projectId || !this.reportDocumentId) {
            return;
        }

        this.loading = true;
        try {
            const { data } = await http.get(`${this.baseUrl}/config`);
            this.config = data || null;
        } catch (ex) {
            if (ex.response?.status === 404) {
                this.config = null;
            } else {
                notify.error(
                    ex.response?.data?.error || "An error occurred while loading the configuration",
                );
            }
        } finally {
            this.loading = false;
        }
    }

    @action
    async save(payload, { announce = true } = {}) {
        if (!this.projectId || !this.reportDocumentId || this.saving) {
            return false;
        }

        this.saving = true;
        try {
            const { data } = await http.put(`${this.baseUrl}/config`, payload);
            this.config = data;
            await this.refreshValidation();
            if (announce) {
                notify.success("Draft saved.");
            }
            return true;
        } catch (ex) {
            notify.error(
                ex.response?.data?.error || "An error occurred while saving the configuration",
            );
            return false;
        } finally {
            this.saving = false;
        }
    }

    @action
    async loadSources() {
        if (!this.projectId || !this.reportDocumentId) {
            return;
        }

        this.loadingSources = true;
        try {
            const { data } = await http.get(`${this.baseUrl}/config/sources`);
            this.sources = data?.list || [];
        } catch (ex) {
            this.sources = [];
        } finally {
            this.loadingSources = false;
        }
    }

    async loadSource(kind, id) {
        if (!this.projectId || !this.reportDocumentId) {
            return null;
        }
        try {
            const { data } = await http.get(`${this.baseUrl}/config/source`, { kind, id });
            return data || null;
        } catch (ex) {
            notify.error(
                ex.response?.data?.error || "An error occurred while loading that configuration",
            );
            return null;
        }
    }

    @action
    async refreshValidation() {
        if (!this.projectId || !this.reportDocumentId) {
            return;
        }
        try {
            const { data } = await http.get(`${this.baseUrl}/config/validation`);
            this.validation = data;
        } catch (ex) {
            this.validation = null;
        }
    }

    @action
    async startRun() {
        if (!this.projectId || !this.reportDocumentId || this.running) {
            return null;
        }

        this.running = true;
        try {
            // The document the run writes into is named by the URL, and the
            // documents to generate from come off the saved config — so no body.
            const { data } = await http.post(`${this.baseUrl}/runs`, {});
            notify.success("Run started! (status: queued)");

            // The run was accepted, but the service could not make sense of part
            // of the config — an unrecognised metadata key, a priority list that
            // did not cover its section, a placeholder-looking value. Say so now:
            // otherwise it only shows up as a wrong document hours later. They are
            // also persisted on the report document, where they survive the
            // wizard closing.
            this.runWarnings = Array.isArray(data?.warnings) ? data.warnings : [];
            if (this.runWarnings.length) {
                notify.warn(
                    `The configuration was accepted with ${this.runWarnings.length} warning(s): ` +
                        this.runWarnings
                            .map((w) => [w.path, w.message].filter(Boolean).join(": "))
                            .join("; "),
                );
            }

            // Let an open document pick up the run it now carries, so its
            // progress banner shows without a reload.
            events.emit("reportDocument.cer.runStarted", this.reportDocumentId);

            const { id } = unwrapRun(data);
            if (id) {
                this.pollRun(id);
            }
            return data;
        } catch (ex) {
            notify.error(
                ex.response?.data?.error ||
                    `An error occurred while starting the ${this.shortName} run`,
            );
            return null;
        } finally {
            this.running = false;
        }
    }

    /**
     * Watch a run to its terminal state, purely to notify.
     *
     * The progress banner on the document itself is driven separately (see
     * `pollCerGeneration` in the report-documents store); this exists because the
     * wizard is opened from project setup, where that banner is not on screen.
     */
    @action
    async pollRun(runId, intervalMs = POLL_INTERVAL_MS) {
        if (!runId || this.polling) {
            return;
        }
        this.polling = true;

        const tick = async () => {
            let data;
            try {
                const res = await http.get(`/cer/runs/${runId}`);
                data = res.data;
            } catch (ex) {
                // Transient error — keep polling, and stay quiet about it.
                this._pollTimer = setTimeout(tick, intervalMs);
                return;
            }

            const { status, errorMessage } = unwrapRun(data);

            if (status === CerGenerationStatus.FAILED) {
                this.stopPolling();
                notify.error(errorMessage || `The ${this.shortName} generation run failed`);
                return;
            }

            if (status === CerGenerationStatus.COMPLETED) {
                if (data?.reportDocumentId) {
                    this.stopPolling();
                    notify.success(
                        `The AI generated ${this.shortName} document has been added to this project.`,
                    );
                    return;
                }
                // The run is done but the backend is still importing its sections.
            }

            this._pollTimer = setTimeout(tick, intervalMs);
        };

        tick();
    }

    @action
    stopPolling() {
        if (this._pollTimer) {
            clearTimeout(this._pollTimer);
            this._pollTimer = null;
        }
        this.polling = false;
    }
}

const cerConfigStore = new CerConfigStore();
export default cerConfigStore;
